import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { Plus, Trash2, Store, MapPin, Pencil } from "lucide-react";
import { toast } from "sonner";
import api from "../../lib/api";
import { EmployerTabs } from "../../components/employer/EmployerTabs";
import { useConfirm } from "../../components/ConfirmProvider";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "../../components/ui/dialog";

const input = "w-full px-4 py-3 rounded-xl border border-stone-border focus:outline-none focus:ring-2 focus:ring-brand bg-white";
const empty = { name: "", address: "", city: "", state: "", zip_code: "", phone: "", contact_email: "" };

export default function Restaurants() {
  const { t } = useTranslation();
  const confirm = useConfirm();
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const load = () => api.get("/restaurants").then(({ data }) => setList(data || [])).catch(() => {}).finally(() => setLoading(false));
  useEffect(() => { load(); }, []);

  const openNew = () => { setEditing(null); setForm(empty); setOpen(true); };
  const openEdit = (r) => { setEditing(r); setForm({ ...empty, ...r }); setOpen(true); };
  const set = (k) => (e) => setForm((f) => ({ ...f, [k]: e.target.value }));

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      if (editing) await api.put(`/restaurants/${editing.id}`, form);
      else await api.post("/restaurants", form);
      toast.success(t("employer.restaurants.saved"));
      setOpen(false);
      load();
    } catch (err) { toast.error(t("common.error")); }
    finally { setSaving(false); }
  };

  const remove = async (r) => {
    if (!(await confirm({ description: `${t("confirm.deleteDesc")}` }))) return;
    try { await api.delete(`/restaurants/${r.id}`); toast.success(t("employer.restaurants.deleted")); load(); }
    catch (err) { toast.error(t("common.error")); }
  };

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
      <h1 className="font-heading text-3xl font-bold text-stone-text mb-6">{t("employer.restaurants.title")}</h1>
      <EmployerTabs />

      <button data-testid="restaurant-add-btn" onClick={openNew} className="flex items-center gap-2 px-5 py-3 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover transition-colors mb-6">
        <Plus className="w-4 h-4" /> {t("employer.restaurants.add")}
      </button>

      {loading ? (
        <div className="py-12 text-center text-stone-muted">{t("jobs.loading")}</div>
      ) : list.length === 0 ? (
        <div className="py-12 text-center text-stone-muted" data-testid="restaurants-empty">{t("employer.restaurants.empty")}</div>
      ) : (
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4" data-testid="restaurants-list">
          {list.map((r) => (
            <div key={r.id} className="bg-white rounded-2xl border border-stone-border shadow-sm p-5 flex items-start justify-between gap-3" data-testid="restaurant-item">
              <div className="flex items-start gap-3 min-w-0">
                <div className="w-11 h-11 rounded-xl bg-brand-soft flex items-center justify-center shrink-0"><Store className="w-5 h-5 text-brand" /></div>
                <div className="min-w-0">
                  <h3 className="font-heading font-semibold text-stone-text truncate">{r.name}</h3>
                  <p className="text-sm text-stone-sub truncate">{r.address}</p>
                  <div className="flex items-center gap-1 text-xs text-stone-muted mt-1"><MapPin className="w-3.5 h-3.5" /> {r.city}, {r.state}</div>
                </div>
              </div>
              <div className="flex gap-1 shrink-0">
                <button data-testid="restaurant-edit-btn" onClick={() => openEdit(r)} className="p-2 rounded-lg hover:bg-brand-soft text-stone-muted hover:text-brand"><Pencil className="w-4 h-4" /></button>
                <button data-testid="restaurant-delete-btn" onClick={() => remove(r)} className="p-2 rounded-lg hover:bg-red-50 text-stone-muted hover:text-red-500"><Trash2 className="w-4 h-4" /></button>
              </div>
            </div>
          ))}
        </div>
      )}

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent data-testid="restaurant-modal">
          <DialogHeader><DialogTitle className="font-heading">{editing ? t("employer.restaurants.edit") : t("employer.restaurants.add")}</DialogTitle><DialogDescription>{t("employer.restaurants.title")}</DialogDescription></DialogHeader>
          <form onSubmit={save} className="space-y-3">
            <input data-testid="rest-name" required placeholder={t("employer.restaurants.name")} className={input} value={form.name} onChange={set("name")} />
            <input data-testid="rest-address" required placeholder={t("employer.restaurants.address")} className={input} value={form.address} onChange={set("address")} />
            <div className="grid grid-cols-2 gap-3">
              <input data-testid="rest-city" required placeholder={t("employer.restaurants.city")} className={input} value={form.city} onChange={set("city")} />
              <input data-testid="rest-state" required placeholder={t("employer.restaurants.state")} className={input} value={form.state} onChange={set("state")} />
              <input data-testid="rest-zip" placeholder={t("employer.restaurants.zip")} className={input} value={form.zip_code} onChange={set("zip_code")} />
              <input data-testid="rest-phone" required placeholder={t("employer.restaurants.phone")} className={input} value={form.phone} onChange={set("phone")} />
            </div>
            <input data-testid="rest-email" placeholder={t("employer.restaurants.email")} className={input} value={form.contact_email} onChange={set("contact_email")} />
            <DialogFooter>
              <button type="submit" data-testid="rest-save" disabled={saving} className="px-5 py-2.5 rounded-xl bg-brand text-white font-semibold hover:bg-brand-hover disabled:opacity-60">{saving ? "..." : t("employer.restaurants.save")}</button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  );
}
